// Cliente HTTP unico. En red precaria lo importante no es la abstraccion sino
// que todo error tenga un mensaje que un usuario pueda entender y reintentar.

const BASE = "/api";
const HEADER_CODIGO = "X-Codigo-Gestion";

class ErrorApi extends Error {
  constructor(mensaje, status, detalles) {
    super(mensaje);
    this.status = status;
    this.detalles = detalles;
  }
}

async function pedir(ruta, { codigo, ...opciones } = {}) {
  let respuesta;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      ...opciones,
      headers: {
        "Content-Type": "application/json",
        ...(codigo && { [HEADER_CODIGO]: codigo }),
        ...opciones.headers,
      },
    });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    throw new ErrorApi("Sin conexion. Revisa tu red e intenta de nuevo.", 0);
  }

  // El API siempre responde JSON. Si llega HTML, no es el API: casi siempre el
  // backend esta caido y el proxy de Vite alcanzo otra app en ese puerto. Sin
  // este chequeo el sintoma era un "Algo salio mal" generico, imposible de
  // diagnosticar desde la interfaz.
  const tipo = respuesta.headers.get("content-type") ?? "";
  if (!tipo.includes("application/json")) {
    throw new ErrorApi(
      `El servidor respondio algo que no es del API (${respuesta.status}). Verifica que el backend este corriendo.`,
      respuesta.status,
    );
  }

  const cuerpo = await respuesta.json().catch(() => ({}));

  if (!respuesta.ok) {
    throw new ErrorApi(
      cuerpo.error ?? "Algo salio mal.",
      respuesta.status,
      cuerpo.detalles,
    );
  }

  return cuerpo;
}

/// Personas y mascotas exponen la misma interfaz; solo cambia el prefijo.
function recurso(base) {
  return {
    feed: ({ cursor, tipo, estado } = {}) => {
      const q = new URLSearchParams();
      if (cursor) q.set("cursor", cursor);
      if (tipo) q.set("tipo", tipo);
      if (estado) q.set("estado", estado);
      return pedir(`/${base}?${q}`);
    },

    mapa: (limites) => pedir(`/${base}/mapa?${new URLSearchParams(limites)}`),

    detalle: (id, codigo) => pedir(`/${base}/${id}`, { codigo }),

    /**
     * Crear reporte. `tokenAntiBot` viaja en un header y no en el cuerpo porque
     * el schema del backend es `.strict()`: una clave extra en el JSON haria
     * fallar la validacion entera.
     */
    crear: (datos, tokenAntiBot) =>
      pedir(`/${base}`, {
        method: "POST",
        body: JSON.stringify(datos),
        headers: tokenAntiBot ? { "X-Turnstile-Token": tokenAntiBot } : {},
      }),

    editar: (id, codigo, campos) =>
      pedir(`/${base}/${id}`, {
        method: "PATCH",
        codigo,
        body: JSON.stringify(campos),
      }),

    cerrar: (id, codigo, estado) =>
      pedir(`/${base}/${id}/estado`, {
        method: "PATCH",
        codigo,
        body: JSON.stringify({ estado }),
      }),

    /// Limites reales que aplica el servidor. Consultarlos en vez de asumirlos:
    /// las constantes del frontend se desincronizan al cambiar el entorno.
    limites: (id) => pedir(`/${base}/${id}/multimedia/limites`),

    /**
     * Sube una foto. Usa XMLHttpRequest y no fetch porque fetch no reporta
     * progreso de subida, y en conexion precaria una barra que no se mueve es
     * indistinguible de una app colgada.
     *
     * El body son los bytes crudos del File. El servidor ignora el content-type
     * y valida por magic bytes.
     */
    subirFoto: (id, codigo, archivo, { onProgreso, signal } = {}) =>
      new Promise((resolver, rechazar) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BASE}/${base}/${id}/multimedia`);
        xhr.setRequestHeader(HEADER_CODIGO, codigo);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");

        xhr.upload.onprogress = (evento) => {
          if (evento.lengthComputable && onProgreso) {
            onProgreso(Math.round((evento.loaded / evento.total) * 100));
          }
        };

        xhr.onload = () => {
          let cuerpo = {};
          try {
            cuerpo = JSON.parse(xhr.responseText);
          } catch {
            // Respuesta no-JSON: casi siempre un proxy o balanceador de por medio.
          }
          if (xhr.status >= 200 && xhr.status < 300) return resolver(cuerpo);
          rechazar(
            new ErrorApi(cuerpo.error ?? "No se pudo subir la foto.", xhr.status),
          );
        };

        xhr.onerror = () =>
          rechazar(new ErrorApi("Sin conexion durante la subida.", 0));
        xhr.ontimeout = () =>
          rechazar(new ErrorApi("La subida tardo demasiado.", 0));
        xhr.onabort = () => rechazar(new ErrorApi("Subida cancelada.", 0));

        signal?.addEventListener("abort", () => xhr.abort(), { once: true });

        // 2 minutos: una foto de 3MB en red mala puede tardar bastante.
        xhr.timeout = 120_000;
        xhr.send(archivo);
      }),
  };
}

function parametrosBusqueda(filtros) {
  const parametros = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (Array.isArray(valor)) {
      if (valor.length) parametros.set(clave, valor.join(","));
      continue;
    }
    if (valor !== undefined && valor !== null && valor !== "") {
      parametros.set(clave, String(valor));
    }
  }
  return parametros;
}

function buscarRecurso(entidad) {
  return (filtros = {}, opciones = {}) =>
    pedir(`/buscar/${entidad}?${parametrosBusqueda(filtros)}`, { signal: opciones.signal });
}

export const api = {
  denuncias: {
    crear: (datos) => pedir("/denuncias", { method: "POST", body: JSON.stringify(datos) }),
  },
  personas: recurso("personas"),
  mascotas: recurso("mascotas"),
  buscar: {
    personas: buscarRecurso("personas"),
    mascotas: buscarRecurso("mascotas"),
  },
};

export { ErrorApi };
